import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, MemoryRouter, useParams } from 'react-router-dom';

import { PerformanceProfilePage, type OwnProfileView } from './PerformanceProfilePage';

import type { SessionUser } from '@/types/session/SessionUser';

import { useGetConfiguration } from '@/api/configuration';
import {
    useGetAllCompanyOrTeamUsers,
    useGetCompanyInformation,
    useGetUserById,
    useGetUserPermissions,
} from '@/api/directory';
import { companyAtom } from '@/atoms/company';
import { FEATURE_FLAGS_UNAVAILABLE, featureFlagsAtom } from '@/atoms/featureFlags';
import { UnavailableState } from '@/components/UnavailableState/UnavailableState';
import { EmbeddedModeContext } from '@/contexts/EmbeddedModeContext';
import { ModuleProviders } from '@/federation/ModuleProviders';
import { useAmelioTranslations } from '@/hooks';
import { WithPermission } from '@/navigation/PermissionGuard';
import { ScrollToTop } from '@/navigation/ScrollToTop';
import { RouteName } from '@/navigation/types';
import { CompetencyProfileWizardPage } from '@/pages/Competencies/CompetencyProfileWizardPage';
import { setCompanyDefaultLanguage } from '@/utils/localization';

// Error code 122 is used for "Access Denied" in the host app
const ACCESS_DENIED_URL = '/error/accessdenied/122?login=1';

interface PerformanceProfileProps {
    employeeId?: string;
    /**
     * Who is reading. `PerformanceShadowHost` has always sent it on this entry point too; until it
     * was declared here the module dropped it, and the page read an empty reader -- no
     * administrator, and notes signed by nobody.
     */
    user?: SessionUser;
    /** Where modals render; see {@link ModuleProviders}. */
    portalTarget?: HTMLElement | null;
    /** Where toasts render; see {@link ModuleProviders}. */
    toastTarget?: HTMLElement | null;
    /**
     * When true, indicates this component is running as an embedded microfrontend.
     * In embedded mode, we wrap in MemoryRouter to provide router context without
     * conflicting with the host's BrowserRouter. In standalone mode (false), we rely
     * on the parent BrowserRouter from App.tsx.
     */
    embedded?: boolean;
    /**
     * Language code passed from the host app (e.g. 'fr', 'en').
     * When provided, the MFE switches its i18n language to match the host.
     */
    language?: string;
}

const PerformanceProfileContent = ({
    employeeId: propEmployeeId,
    embedded,
}: Pick<PerformanceProfileProps, 'employeeId'> & { embedded: boolean }) => {
    // The module's own route names the employee in the address rather than in a prop: read it here so
    // the scope check below guards that route as well as the host's.
    const { id: routeId } = useParams<{ id?: string }>();
    const employeeId = propEmployeeId || routeId;
    const { t } = useAmelioTranslations();
    const client = useQueryClient();
    const { data: permissions, isError: isPermissionsError } = useGetUserPermissions();
    const { data: company } = useGetCompanyInformation({ enabled: embedded });
    const { data: currentUser, isError: isCurrentUserError } = useGetUserById({ userId: 'me' });
    const setCompany = useSetAtom(companyAtom);
    // Embedded, the module's `App` never mounts, so nothing else loads the feature flags here.
    const { data: configuration, error: configurationError } = useGetConfiguration({
        enabled: embedded,
    });
    const setFeatureFlags = useSetAtom(featureFlagsAtom);

    useEffect(() => {
        if (company && embedded) {
            setCompany(company);
            if (company.company?.defaultLanguage) {
                setCompanyDefaultLanguage(company.company.defaultLanguage);
            }
        }
    }, [company, embedded, setCompany]);

    useEffect(() => {
        // Unavailable rather than empty: a failed request says nothing about what the organization
        // has, and the gates offer a retry instead of announcing a feature is off (see `App`).
        if (configurationError) {
            setFeatureFlags(FEATURE_FLAGS_UNAVAILABLE);
        }
        if (configuration) {
            setFeatureFlags(configuration.featureFlags ?? []);
        }
    }, [configuration, configurationError, setFeatureFlags]);

    // Check if user is viewing their own profile
    const isViewingSelf = currentUser?.id === employeeId;

    // Base permission: must have canViewPerformanceProfile
    // For viewing others: also need canManageTeamPerformance or canManageAllDepartmentsPerformance
    // For viewing self: only need canViewPerformanceProfile
    const hasPermission =
        permissions === undefined || currentUser === undefined
            ? undefined
            : permissions.canViewPerformanceProfile &&
              (isViewingSelf ||
                  permissions.canManageTeamPerformance ||
                  permissions.canManageAllDepartmentsPerformance);

    // Without the performance profile, every employee still reads their own profile for the
    // objectives they hold, and, when they manage their own objectives, for their competencies.
    const mayReadOwnObjectives = hasPermission === false && isViewingSelf;
    const ownView = ((): OwnProfileView | undefined => {
        if (!mayReadOwnObjectives) return undefined;
        if (permissions?.canEditOwnObjectives) return 'objectivesAndCompetencies';
        return 'objectives';
    })();

    const isOrgWide = permissions?.canManageAllDepartmentsPerformance ?? false;
    const needsScopeCheck = hasPermission === true && !isOrgWide && !isViewingSelf && !!employeeId;

    const { data: scopedUsers, isError: isScopeError } = useGetAllCompanyOrTeamUsers({
        enabled: needsScopeCheck,
        canManageAllDepartmentsPerformance: false,
        canManageTeamPerformance: needsScopeCheck,
        // Include self so users can always view their own Performance Profile
        includeSelf: true,
    });

    const hasAccess = (() => {
        if (hasPermission === undefined) return undefined;
        if (mayReadOwnObjectives) return true;
        if (!hasPermission) return false;
        // Self-view is always allowed (with canViewPerformanceProfile)
        if (isViewingSelf) return true;
        if (isOrgWide) return true;
        if (!employeeId) return true;
        if (scopedUsers === undefined) return undefined;
        return scopedUsers.some((user) => user.id === employeeId);
    })();

    // Redirect to access denied page if user doesn't have permission
    useEffect(() => {
        if (hasAccess === false) {
            window.location.href = ACCESS_DENIED_URL;
        }
    }, [hasAccess]);

    // The same for the three reads that settle whether this profile opens at all: failed, they stay
    // undefined for good, which the gate below cannot tell from "still loading". And a failure is
    // not a refusal -- sending the reader to the access-denied page would deny them a right they
    // may well hold, so they are asked to try again instead.
    if (isPermissionsError || isCurrentUserError || isScopeError) {
        return (
            <UnavailableState
                title={t('errors.profileAccessUnavailableTitle')}
                message={t('errors.profileAccessUnavailableMessage')}
                // Every query of this tree is one the gate is waiting on; the page mounts once they answer.
                onRetry={() => {
                    client.refetchQueries();
                }}
            />
        );
    }

    // Show nothing while loading or redirecting
    if (hasAccess === undefined || hasAccess === false) {
        return null;
    }
    return (
        <PerformanceProfilePage
            employeeId={employeeId}
            ownView={ownView}
        />
    );
};

export const PerformanceProfile = ({
    employeeId,
    user,
    portalTarget,
    toastTarget,
    embedded = false,
    language,
}: PerformanceProfileProps) => {
    const { i18n } = useTranslation();

    useEffect(() => {
        if (language && language !== i18n.language) {
            i18n.changeLanguage(language);
        }
    }, [language, i18n]);

    const router = embedded ? (
        <MemoryRouter>
            <ScrollToTop />
            <Switch>
                <Route
                    exact
                    path={RouteName.CompetencyProfileCreate}
                    render={() => (
                        <WithPermission permissionKey={'canManageCompetencies'}>
                            <CompetencyProfileWizardPage />
                        </WithPermission>
                    )}
                />

                <Route
                    render={() => (
                        <PerformanceProfileContent
                            employeeId={employeeId}
                            embedded={embedded}
                        />
                    )}
                />
            </Switch>
        </MemoryRouter>
    ) : (
        <Switch>
            <Route
                exact
                path={RouteName.CompetencyProfileCreate}
                render={() => (
                    <WithPermission permissionKey={'canManageCompetencies'}>
                        <CompetencyProfileWizardPage />
                    </WithPermission>
                )}
            />

            <Route
                render={() => (
                    <PerformanceProfileContent
                        employeeId={employeeId}
                        embedded={embedded}
                    />
                )}
            />
        </Switch>
    );

    return (
        <EmbeddedModeContext.Provider value={embedded}>
            <ModuleProviders
                user={user}
                portalTarget={portalTarget}
                toastTarget={toastTarget}
            >
                {router}
            </ModuleProviders>
        </EmbeddedModeContext.Provider>
    );
};

export default PerformanceProfile;
